import { faPenToSquare, faCircleQuestion, faMagnifyingGlass, faPersonChalkboard, faRightFromBracket, faUser, faUsers, faXmark, faHouse, faPeopleArrows } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useLogout from "hooks/useLogout";
import useUserData from "hooks/useUserData";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import SearchBox from "features/Search/SearchBox";
import "./Header.scss";
import useBranding from "hooks/useBranding";
import { UserRoles, UserRoleIds } from "utils/userRoles";
import Spoofing from "../Spoofing/Spoofing";
import { useSpoofing } from "../Spoofing/hooks/useSpoofing";

const Header = ({ wideHeader }) => {
	const { logo } = useBranding();
	const [sidebarToggle, setSidebarToggle] = useState(false);
	const [mobileSearchToggle, setMobileSearchToggle] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { logout } = useLogout(navigate);
	const { userData } = useUserData();
	const guruUrls = (sessionStorage.gurus) ? JSON.parse(sessionStorage.gurus) : [];

	const sidebarRef = useRef(null); // Reference to the sidebar
	const hamburgerRef = useRef(null); // Reference to the hamburger button

	//Spoofing
	const [userCanSpoof, setUserCanSpoof] = useState(false);
	const [spoofingToggle, setSpoofingToggle] = useState(false);
	const [isSpoofing, setIsSpoofing] = useState(false);
	const { handleStopSpoofingClick } = useSpoofing();

	const closeSidebar = () => {
		setSidebarToggle(false);
	};

	const toggleSidebar = () => {
		setSidebarToggle(!sidebarToggle);
	};

	const handleHamburgerClick = () => {
		if (sidebarToggle || spoofingToggle) {
			setSidebarToggle(false);
			setSpoofingToggle(false);
		} else {
			setSidebarToggle(true);
		}
	};

	useEffect(() => {
		// Check whether the Spoofing component should be rendered
		const isSpoofed = sessionStorage.getItem('authSpoofer') !== null;
		setIsSpoofing(isSpoofed);

		const permissions = sessionStorage.getItem('permissions') || '';
		const hasViewAnyCompanyPermission = permissions
			.split(',')
			.map(p => p.trim())
			.includes('ViewAnyCompany');

		setUserCanSpoof(isSpoofed || hasViewAnyCompanyPermission);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			if (sidebarToggle) {
				closeSidebar();
			}
		};

		const handleClickOutside = (event) => {
			if (
				sidebarRef.current &&
				!sidebarRef.current.contains(event.target) &&
				hamburgerRef.current &&
				!hamburgerRef.current.contains(event.target)
			) {
				closeSidebar();
			}
		};

		if (sidebarToggle) {
			window.addEventListener("scroll", handleScroll);
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			window.removeEventListener("scroll", handleScroll);
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [sidebarToggle]);

	const toggleMobileSearch = () => {
		setMobileSearchToggle(!mobileSearchToggle);
	};

	const handleSearch = (searchValue) => {
		navigate("/searchresults", { state: searchValue });
	};

	const handleLogout = () => {
		logout();
	};

	const handleDashboardClick = () => {
		navigate(location.pathname === "/dashboard" ? 0 : "/dashboard");
	};

	const handleSpoofingClick = () => {
		setSpoofingToggle(!spoofingToggle);
	};

	const renderGuruUrls = () => {
		if (!guruUrls.length) {
			return null;
		}
		if (guruUrls.length === 1 && guruUrls[0].UserHasGuru) {
			return (
				<div className="sidebar-links-link">
					<FontAwesomeIcon icon={faPersonChalkboard} />
					<Link className="sidebar-links-link-text" to={guruUrls[0].GuruUrl} onClick={toggleSidebar} target="_blank">
						Book a Guru
					</Link>
				</div>
			);
		}

		return (guruUrls.map(guru => (
			guru.UserHasGuru && (
				<div key={guru.Id} className="sidebar-links-link">
					<FontAwesomeIcon icon={faPersonChalkboard} />
					<Link className="sidebar-links-link-text" to={guru.GuruUrl} onClick={toggleSidebar} target="_blank">
						Book a {guru.Name} Guru
					</Link>
				</div>
			)
		)));
	};

	if (!userData || !userData.Name) {
		return null;
	}

	return (
		<div className={`header-container ${wideHeader ? 'wide-header' : 'gutters'}`}>
			<div className={`header ${mobileSearchToggle ? 'hide' : ''} ${isSpoofing ? "is-spoofed" : ""}`}>
				<div className="header-logo" onClick={handleDashboardClick}>
					<img alt="UCSkills" src={logo} />
				</div>

				<div className="header-home">
					<div onClick={handleDashboardClick}>
						<FontAwesomeIcon icon={faHouse} />
					</div>
				</div>

				<div className="header-search">
					<div className="header-search-mobile" onClick={toggleMobileSearch}>
						<FontAwesomeIcon icon={faMagnifyingGlass} />
					</div>
					<div className="header-search-desktop">
						<SearchBox handleClick={handleSearch} />
					</div>
				</div>

				{userCanSpoof && <div className="header-spoofing">
					{isSpoofing && (
						<div className="header-spoofing-name">
							<p>
								{userData.Name}
								<FontAwesomeIcon icon={faXmark} onClick={handleStopSpoofingClick} />
							</p>
						</div>
					)}
					<div className={`header-spoofing-icon ${isSpoofing ? "is-spoofed" : ""}`} onClick={handleSpoofingClick}>
						<div className="header-spoofing-icon-container" ><FontAwesomeIcon icon={faPeopleArrows} /></div>
					</div>
				</div>}

				<div
					className={`header-hamburger ${sidebarToggle || spoofingToggle ? 'header-hamburger-open' : ''}`}
					onClick={handleHamburgerClick}
					ref={hamburgerRef}
				>
					<span className="header-hamburger-bar header-hamburger-bar-one"></span>
					<span className="header-hamburger-bar header-hamburger-bar-two"></span>
					<span className="header-hamburger-bar header-hamburger-bar-three"></span>
				</div>

				{/*Sidebar*/}
				<div className={`sidebar ${sidebarToggle ? 'sidebar-open' : ''}`} ref={sidebarRef}>
					<Link className="sidebar-links-link-text" to="/profile" onClick={toggleSidebar}><h3>{userData.Name}</h3></Link>
					<p>{userData.Email}</p>
					<p>{userData.Company}</p>
					<div className="sidebar-links">
						<div className="sidebar-links-link">
							<FontAwesomeIcon icon={faUser} />
							<Link className="sidebar-links-link-text" to="/profile" onClick={toggleSidebar}>
								My Account
							</Link>
						</div>
						{userData.UserLevel !== UserRoles.Learner &&
							<div className="sidebar-links-link">
								<FontAwesomeIcon icon={faUsers} />
								<Link className="sidebar-links-link-text" to="/usermanagement" onClick={toggleSidebar}>
									User Management
								</Link>
							</div>
						}
						{(userData.UserLevel === UserRoles.SystemAdmin || userData.RoleId === UserRoleIds.ContentCreator || userData.RoleId === UserRoleIds.ContentPublisher) &&
							<div className="sidebar-links-link">
								<FontAwesomeIcon icon={faPenToSquare} />
								<Link className="sidebar-links-link-text" to="/contentcreation" onClick={toggleSidebar}>
									Edit Courses
								</Link>
							</div>
						}
						{renderGuruUrls()}
						<div className="sidebar-links-link">
							<FontAwesomeIcon icon={faCircleQuestion} />
							<Link className="sidebar-links-link-text" to="/help" onClick={toggleSidebar}>
								Help
							</Link>
						</div>
						<div className="sidebar-links-link">
							<FontAwesomeIcon icon={faRightFromBracket} className="secondary" />
							<Link className="sidebar-links-link-text secondary" onClick={() => { toggleSidebar(); handleLogout(); }}>
								Sign Out
							</Link>
						</div>
					</div>
				</div>
			</div>

			<div className={`mobileSearch gutters ${mobileSearchToggle ? 'show' : ''}`}>
				<div className="mobileSearch-search">
					<SearchBox handleClick={handleSearch} />
				</div>
				<div className="mobileSearch-close" onClick={toggleMobileSearch}>
					<FontAwesomeIcon icon={faXmark} />
				</div>
			</div>

			{userCanSpoof && <Spoofing toggle={spoofingToggle} currentUser={userData} />}
		</div>
	);
};

export default Header;
